import React, { useEffect } from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, AlertCircle, CheckCircle, ArrowRight, Car, FileText, Banknote } from 'lucide-react';
import { Button } from '@/components/ui/button';

const CarFinanceMisSellingPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const financeTypes = [
    {
      title: "PCP (Personal Contract Purchase)",
      description: "The most common type of car finance where you pay monthly with a final 'balloon' payment option.",
      link: "/pcp-car-finance-claims"
    },
    {
      title: "HP (Hire Purchase)",
      description: "Fixed monthly payments that pay off the full car value. You own the car once payments are complete.",
      link: "/hp-claims"
    },
    {
      title: "GAP Insurance",
      description: "Guaranteed Asset Protection insurance that covers the difference between your car's value and what you owe.",
      link: "/gap-insurance-claims"
    }
  ];

  const misSellingIndicators = [
    "You weren't told about commission the dealer received",
    "The total amount payable wasn't clearly explained",
    "You weren't offered alternative finance options",
    "The interest rate was higher than it should have been",
    "You felt pressured into taking finance",
    "Important terms and conditions weren't explained",
    "The finance wasn't suitable for your circumstances",
    "You weren't given time to consider the agreement"
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-24">
        {/* Hero Section */}
        <section className="py-12 lg:py-16 bg-background">
          <div className="section-container">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/30 mb-6">
                <Shield className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-gold">SRA Regulated Law Firm</span>
              </div>
              
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6 leading-tight">
                Car Finance Mis-selling
              </h1>
              
              <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                Millions of UK drivers may have been mis-sold car finance. The FCA estimates the industry 
                is estimated to pay out £7.5 billion in compensation. Find out if you're eligible.
              </p>
            </div>
          </div>
        </section>

        {/* What is Mis-selling Section */}
        <section className="py-16 lg:py-20 bg-muted/30">
          <div className="section-container">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-6">
                  What is Car Finance Mis-selling?
                </h2>
                <div className="prose prose-lg max-w-none space-y-4">
                  <p className="text-foreground/80 leading-relaxed">
                    Car finance mis-selling occurs when dealers or lenders fail to properly disclose 
                    important information about your finance agreement, or when the finance sold 
                    wasn't suitable for your needs.
                  </p>
                  <p className="text-foreground/80 leading-relaxed">
                    The most common form of mis-selling involves hidden commission arrangements. 
                    Car dealers often received commission from lenders for arranging your finance — 
                    and in many cases, this was never disclosed to you.
                  </p>
                  <p className="text-foreground/80 leading-relaxed">
                    The FCA found that these secret commission payments often led to higher interest 
                    rates. The dealer had an incentive to put you on a more expensive deal because 
                    it meant more commission for them.
                  </p>
                </div>
              </div>
              
              <Card className="border-border shadow-lg">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center">
                      <AlertCircle className="w-6 h-6 text-destructive" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-foreground">
                      Signs You Were Mis-sold
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {misSellingIndicators.map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                        <span className="text-foreground/80 text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Finance Types Section */}
        <section className="py-16 lg:py-20 bg-background">
          <div className="section-container">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-4">
                Types of Car Finance We Handle
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                We can help you claim compensation for any type of car finance mis-selling.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {financeTypes.map((type, index) => (
                <Card key={index} className="border-border hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                      <Car className="w-6 h-6 text-accent" />
                    </div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      {type.title}
                    </h3>
                    <p className="text-foreground/70 text-sm leading-relaxed mb-4">
                      {type.description}
                    </p>
                    <a 
                      href={type.link} 
                      className="text-sm font-medium text-accent hover:text-accent/80 inline-flex items-center gap-1"
                    >
                      Learn more <ArrowRight className="w-4 h-4" />
                    </a>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Compensation Section */}
        <section className="py-16 lg:py-20 bg-muted/30">
          <div className="section-container">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-4">
                  How Much Compensation Could You Receive?
                </h2>
              </div>
              
              <div className="grid sm:grid-cols-3 gap-6 mb-12">
                <Card className="border-border text-center">
                  <CardContent className="p-6">
                    <Banknote className="w-10 h-10 text-gold mx-auto mb-3" />
                    <p className="text-3xl font-bold text-foreground mb-1">£830*</p>
                    <p className="text-sm text-muted-foreground">Average Claim Per Agreement</p>
                  </CardContent>
                </Card>
                
                <Card className="border-border text-center">
                  <CardContent className="p-6">
                    <FileText className="w-10 h-10 text-accent mx-auto mb-3" />
                    <p className="text-3xl font-bold text-foreground mb-1">£7.5bn</p>
                    <p className="text-sm text-muted-foreground">Owed by Lenders</p>
                  </CardContent>
                </Card>
                
                <Card className="border-border text-center">
                  <CardContent className="p-6">
                    <Shield className="w-10 h-10 text-success mx-auto mb-3" />
                    <p className="text-3xl font-bold text-foreground mb-1">2007</p>
                    <p className="text-sm text-muted-foreground">Claims back to</p>
                  </CardContent>
                </Card>
              </div>
              
              <p className="text-center text-foreground/70 mb-8">
                The exact amount depends on factors including the commission paid, the loan amount, 
                and the interest rate. Many people have multiple agreements and can claim for each one.
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-20 bg-primary">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary-foreground mb-6">
                Find Out If You're Eligible
              </h2>
              <p className="text-lg text-primary-foreground/80 mb-8">
                Our Find Your Agreements tool can locate all your car finance agreements back to 2007. 
                All you need is your name, date of birth, and current address.
              </p>
              <Button variant="gold" size="xl" asChild>
                <a href="https://signup.bluelionclaims.co.uk/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
                  Find Your Agreements
                  <ArrowRight className="w-5 h-5" />
                </a>
              </Button>
              <p className="text-sm text-primary-foreground/60 mt-4">
                No win, no fee. No lender paperwork needed.
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CarFinanceMisSellingPage;
